import React, { useEffect, useState } from "react";
import { ChatCircleText, CaretDown, CaretUp, Question, CheckSquare, Square } from "@phosphor-icons/react";

const STORE_PREFIX = "tiger:hints-collapsed:";

/**
 * Static curated interview hints for the selected protocol.
 * Nurses can tick off questions as they ask them (client-side, ephemeral).
 * Preferred over an AI panel when the nurse wants a reliable reproducible list.
 */
export default function InterviewHintsPanel({ protocol }) {
  const hints = protocol?.interview_hints || [];
  const key = protocol?.protocol_id ? `${STORE_PREFIX}${protocol.protocol_id}` : null;

  const [collapsed, setCollapsed] = useState(false);
  const [asked, setAsked] = useState({});

  // Reset the "asked" state whenever the selected protocol changes
  useEffect(() => {
    setAsked({});
    if (!key) return;
    try {
      const stored = localStorage.getItem(key);
      setCollapsed(stored === "1");
    } catch (err) {
      console.warn("[hints] localStorage read failed", err);
    }
  }, [key]);

  const toggleCollapse = () => {
    setCollapsed((c) => {
      const next = !c;
      if (key) {
        try { localStorage.setItem(key, next ? "1" : "0"); }
        catch (err) { console.warn("[hints] localStorage write failed", err); }
      }
      return next;
    });
  };

  if (!protocol || hints.length === 0) return null;

  const answeredCount = Object.values(asked).filter(Boolean).length;

  return (
    <div
      data-testid="interview-hints-panel"
      className="rounded-md border border-teal-200 bg-gradient-to-br from-teal-50 to-white overflow-hidden"
    >
      <button
        type="button"
        onClick={toggleCollapse}
        data-testid="interview-hints-toggle"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-teal-100/60 hover:bg-teal-100 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ChatCircleText size={18} weight="fill" className="text-teal-700 shrink-0" />
          <div className="text-left min-w-0">
            <div className="font-heading text-sm font-semibold text-teal-900 truncate">
              Suggerimenti intervista
            </div>
            <div className="text-[10px] uppercase tracking-widest text-teal-700/80 font-semibold">
              {hints.length} domande utili · {answeredCount}/{hints.length} spuntate
            </div>
          </div>
        </div>
        {collapsed ? <CaretDown size={16} className="text-teal-700" /> : <CaretUp size={16} className="text-teal-700" />}
      </button>

      {!collapsed && (
        <ol className="p-3 space-y-1.5">
          {hints.map((h, i) => {
            const ticked = !!asked[i];
            const Box = ticked ? CheckSquare : Square;
            return (
              <li key={`hint-${i}`}>
                <button
                  type="button"
                  data-testid={`interview-hint-${i}`}
                  onClick={() => setAsked((cur) => ({ ...cur, [i]: !cur[i] }))}
                  className={`w-full flex items-start gap-2 text-left rounded-md px-3 py-2 transition-colors ${
                    ticked ? "bg-teal-50/80 text-teal-800" : "bg-white text-slate-800 hover:bg-teal-50/40"
                  }`}
                >
                  <Box size={16} weight={ticked ? "fill" : "regular"} className={`shrink-0 mt-0.5 ${ticked ? "text-teal-600" : "text-slate-400"}`} />
                  <span className={`text-sm leading-snug ${ticked ? "line-through opacity-70" : ""}`}>
                    <span className="text-[10px] font-mono text-slate-400 mr-1">{i + 1}.</span>
                    {h}
                  </span>
                </button>
              </li>
            );
          })}
          <li className="text-[10px] text-teal-700/70 mt-2 flex items-center gap-1 pt-1 border-t border-teal-100">
            <Question size={11} weight="bold" /> Suggerimenti didattici · non esaustivi
          </li>
        </ol>
      )}
    </div>
  );
}
